import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { formatPrice } from '@anytag/shared-utils';
import { useToggleTableColumns } from '@anytag/shared-hooks';
import { TableCell, ColumnCustomiser } from '@anytag/shared-atoms';
import { formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { TiktokSpecialCampaignReport, TikTokSpecialReportOrderField } from '@anytag/graphql';
import { InfluencerPost } from '../influencer-post';
import { TikTokCampaignReportRowData, TikTokCampaignReportTableStatKeys } from '../types';

const CellAdValue = ({ value, adValue }: { value?: number | null; adValue?: number | null }) => {
  const { t } = useTranslation();

  return (
    <>
      {formatIntNumber(value)}
      {!!adValue && (
        <>
          <br />
          <span className="text-sm leading-[100%] font-normal text-attention-green">
            <span className="box-border rounded-[10px] bg-attention-green px-1 py-0.5 text-xxs font-semibold text-white">
              {t('Ad')}
            </span>
            &nbsp;
            <span>+{formatIntNumber(adValue)}</span>
          </span>
        </>
      )}
    </>
  );
};

interface Props {
  data: TiktokSpecialCampaignReport;
}

export const useTikTokCampaignReportTable = ({ data }: Props) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { getSelectableColumns, handleToggleColumns } = useToggleTableColumns<
    TikTokCampaignReportRowData,
    TikTokCampaignReportTableStatKeys
  >();

  const currency = t(data.currency || '');

  const columns: ColumnDef<TikTokCampaignReportRowData>[] = [
    {
      accessorKey: 'title',
      enableHiding: false,
      meta: { sticky: true },
      header: ({ table }) => {
        const { selectableOptions } = getSelectableColumns(table);

        return (
          <div className="flex items-center justify-between gap-2">
            {t('HeaderColumn.Total Post')}
            <ColumnCustomiser
              options={selectableOptions}
              onChange={handleToggleColumns(table)}
              savedValuesKey="tiktokReportTableColumns"
              buttonProps={{
                tooltip: { place: 'top', help: <span className="text-sm">{t('Button.Customise report table')}</span> }
              }}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const { date, className, influencerInfo, postInfo } = row.original;

        return className === 'total-row' ? (
          <TableCell.CampaignTotal count={data.postCount} unit={t('Post')} />
        ) : className === 'average-row' ? (
          <TableCell.CampaignAverage title={t('Average')} />
        ) : date ? (
          <TableCell.Text className="text-right" value={date} />
        ) : (
          <InfluencerPost influencerInfo={influencerInfo} postInfo={postInfo} />
        );
      }
    },
    {
      accessorKey: 'cost',
      enableHiding: false,
      header: t<string>('HeaderColumn.Cost'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costHelp'),
        sortField: TikTokSpecialReportOrderField.COST
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats.cost ? currency : ''}
          value={formatPrice(row.original.stats.cost || null, currency)}
        />
      )
    },
    {
      accessorKey: 'followers',
      enableHiding: false,
      header: t<string>('HeaderColumn.Follower'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.UniqueReachHelp'),
        sortField: TikTokSpecialReportOrderField.FOLLOWERS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.followers)} />
    },
    {
      accessorKey: 'engagement',
      enableHiding: false,
      header: t<string>('HeaderColumn.Engagement'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.TotalEngagementsHelp'),
        sortField: TikTokSpecialReportOrderField.ENGAGEMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.engagement)} />
    },
    {
      accessorKey: 'engagementRate',
      enableHiding: false,
      header: t<string>('HeaderColumn.Egg Rate by followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementRateHelp'),
        sortField: TikTokSpecialReportOrderField.ENGAGEMENT_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.engagementRate, false)} unit="%" />
    },
    {
      accessorKey: 'engagementViewsRate',
      header: t<string>('HeaderColumn.Egg Rate by views'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementViewsRateHelp'),
        sortField: TikTokSpecialReportOrderField.ENGAGEMENT_VIEWS_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementViewsRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'views',
      enableHiding: false,
      header: t<string>('HeaderColumn.Video view'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.videoViewHelp'),
        sortField: TikTokSpecialReportOrderField.VIEWS
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={<CellAdValue value={row.original.stats.views} adValue={row.original.stats.videoPlayActions} />}
        />
      )
    },
    {
      accessorKey: 'adImpressions',
      enableHiding: false,
      header: t<string>('HeaderColumn.Ad Impression'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.adImpressionHelp'),
        sortField: TikTokSpecialReportOrderField.AD_IMPRESSIONS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.adImpressions)} />
    },

    {
      accessorKey: 'adImpressionsRate',
      header: t<string>('HeaderColumn.Ad Impression Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.adImpressionsRateHelp'),
        sortField: TikTokSpecialReportOrderField.AD_IMPRESSIONS_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.adImpressionsRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'adReach',
      enableHiding: false,
      header: t<string>('HeaderColumn.Ad Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.adReachHelp'),
        sortField: TikTokSpecialReportOrderField.AD_REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.adReach)} />
    },
    {
      accessorKey: 'adReachRate',
      header: t<string>('HeaderColumn.Ad Reach Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.adReachRateHelp'),
        sortField: TikTokSpecialReportOrderField.AD_REACH_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats.adReachRate, false)} unit="%" />
    },
    {
      accessorKey: 'likes',
      enableHiding: false,
      header: t<string>('HeaderColumn.Like'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.LikeReactionHelp'),
        sortField: TikTokSpecialReportOrderField.LIKES
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={<CellAdValue value={row.original.stats.likes} adValue={row.original.stats.adLikes} />}
        />
      )
    },
    {
      accessorKey: 'comments',
      enableHiding: false,
      header: t<string>('HeaderColumn.Comment'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.commentHelp'),
        sortField: TikTokSpecialReportOrderField.COMMENTS
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={<CellAdValue value={row.original.stats.comments} adValue={row.original.stats.adComments} />}
        />
      )
    },
    {
      accessorKey: 'shares',
      enableHiding: false,
      header: t<string>('HeaderColumn.Share'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.shareHelp'),
        sortField: TikTokSpecialReportOrderField.SHARES
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={<CellAdValue value={row.original.stats.shares} adValue={row.original.stats.adShares} />}
        />
      )
    },
    {
      accessorKey: 'costsPerFollower',
      header: t<string>('HeaderColumn.Cost / Follower'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costsPerFollowerHelp'),
        sortField: TikTokSpecialReportOrderField.COSTS_PER_FOLLOWER
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerFollower || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerView',
      header: t<string>('HeaderColumn.Cost / Video view'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costsPerViewHelp'),
        sortField: TikTokSpecialReportOrderField.COSTS_PER_VIEW
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerView || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerEngagement',
      header: t<string>('HeaderColumn.Cost / Engagement'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costsPerEngagementHelp'),
        sortField: TikTokSpecialReportOrderField.COSTS_PER_ENGAGEMENT
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatPrice(row.original.stats.costsPerEngagement || null, currency)}
          unit={currency}
        />
      )
    },
    {
      accessorKey: 'costsPerLike',
      header: t<string>('HeaderColumn.Cost / Like'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costsPerLikeHelp'),
        sortField: TikTokSpecialReportOrderField.COSTS_PER_LIKE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerLike || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerComment',
      header: t<string>('HeaderColumn.Cost / Comment'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costsPerCommentHelp'),
        sortField: TikTokSpecialReportOrderField.COSTS_PER_COMMENT
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerComment || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerShare',
      header: t<string>('HeaderColumn.Cost / Share'),
      meta: {
        align: 'right',
        hidden: !isAdminStaff,
        help: t<string>('Tooltip.costsPerShareHelp'),
        sortField: TikTokSpecialReportOrderField.COSTS_PER_SHARE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats.costsPerShare || null, currency)} unit={currency} />
      )
    }
  ];

  return { columns };
};
